// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InvoiceFilter from "@hardwoods/App/Components/InvoicePayment/Filter";
import { invoicePaymentInvoiceModel } from "@hardwoods/App/Services/InvoicePaymentService";
import { FilterName, InvoicesFilters } from "@hardwoods/App/Widgets/InvoicePayment/InvoicePayment";
import { areObjectValuesNotEmpty } from "@hardwoods/XDL/Utilities/helper";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Filter from "@insite/mobius/Icons/Filter";
import { Clickable, Icon, LoadingOverlay, Typography } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Notify from "@rugby/App/Components/Notify";
import xGetLateFeeTotal from "@rugby/App/Handlers/InvoicePayment/GetLateFeeTotal";
import xGetOpenInvoicePayment from "@rugby/App/Handlers/InvoicePayment/InvoicePayment";
import xMakePaymentInvoicePayment from "@rugby/App/Handlers/InvoicePayment/MakePayment";
import { CustomAppState } from "@rugby/App/Store/Reducers";
import { getAchError, getIsEditing, getSelectedAch } from "@rugby/App/Store/Selectors/AchSelectors";
import { getLateFee, getPaymentMethod } from "@rugby/App/Store/Selectors/InvoicePaymentSelectors";
import CreditCardIFrame from "@rugby/App/Widgets/InvoicePayment/CreditCardIFrame";
import {
    invoicePaymentStyle,
    invoicePaymentTotalElementStyle,
} from "@rugby/App/Widgets/InvoicePayment/InvoicePayment.style";
import { titleStyles } from "@rugby/Overrides/Widgets/Basic/PageTitle";
import { capitalize, detectIsAuthenticated } from "@rugby/XDL/Utilities";
import { initializeScrollTo } from "@rugby/XDL/Utilities/xng.scroller";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const settings: any = getSettingsCollection(state);
    const session = getSession(state);
    const applyLateFeeOnItems = settings?.customSettings?.applyLateFeeOn;
    const achPaymentMethodName = settings?.customSettings?.achPaymentMethodName
        ? settings?.customSettings?.achPaymentMethodName
        : "";
    const paymentMethod = getPaymentMethod(state) as PaymentMethodDto;
    const currentCurrencySymbol = state.context.session.currency?.currencySymbol;

    return {
        invoicePaymentResponse: state.xInvoicePayment,
        settings,
        applyLateFeeOnItems,
        cart,
        billTo: getBillToState(state, session.billToId).value,
        pageTitle: getCurrentPage(state).fields.title,
        session,
        paymentMethod,
        lateFee: getLateFee(state),
        selectedAch: getSelectedAch(state),
        achError: getAchError(state),
        achIsEditing: getIsEditing(state),
        achPaymentMethodName,
        paymentMethodName: paymentMethod?.name,
        currentCurrencySymbol,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    xGetOpenInvoicePayment,
    xGetLateFeeTotal,
    xMakePaymentInvoicePayment,
    loadCurrentCart,
};

const style = invoicePaymentStyle;

const TotalElement: FC<{ title: string; value: string; dataTestSelector: string; isError?: boolean }> = ({
    title,
    value,
    dataTestSelector,
    isError,
}) => {
    const style = invoicePaymentTotalElementStyle;
    return (
        <StyledWrapper data-test-selector={dataTestSelector} {...style.wrapper}>
            <StyledWrapper {...style.header.wrapper}>
                <Typography {...style.header.title}>{title}</Typography>
            </StyledWrapper>
            <StyledWrapper {...style.content.wrapper}>
                <Typography {...style.content.text} {...{ isError }}>
                    {value}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const InvoicePaymentWidget: FC<Props> = ({
    cart,
    settings,
    session,
    applyLateFeeOnItems,
    paymentMethod,
    lateFee,
    loadCurrentCart,
    billTo,
    pageTitle,
    invoicePaymentResponse,
    xMakePaymentInvoicePayment,
    xGetOpenInvoicePayment,
    xGetLateFeeTotal,
    selectedAch,
    achError,
    achIsEditing,
    paymentMethodName,
    achPaymentMethodName,
    currentCurrencySymbol,
}) => {
    const childRef = useRef<any>();
    const selectedAchAccountNumber = selectedAch?.accountNumber;

    const [errorApi, setApiError] = useState("");
    const [paymentAmountValues, setPaymentAmountValues] = useState<{ [key: string]: any }>({});
    const [paymentAmountErrors, setPaymentAmountErrors] = useState<{ [key: string]: string }>({});
    const prevPaymentOptions = usePrevious(cart?.paymentOptions);
    const [customPaymentMethods, setCustomPaymentMethods] = useState<PaymentMethodDto[]>([]);
    const [noAmountsEnteredError, setNoAmountsEnteredError] = useState("");
    const [remarkModal, setRemarkModal] = useState({ isOpen: false, invoiceNumber: "", remark: "" });

    // filters
    const [invoices, setInvoices] = useState<invoicePaymentInvoiceModel[]>([]);
    const [openedFilter, setOpenedFilter] = useState("");
    const initalInvoiceFilter = {
        invoiceNumber: [],
        invoiceDate: [],
        dueDate: [],
        customerPo: [],
        shipTo: [],
        balanceDueDisplay: [],
        termsDiscountDate: [],
    };
    const [invoicesFilters, setInvoicesFilters] = useState<InvoicesFilters>(initalInvoiceFilter);
    const [selectedInvoicesFilters, setSelectedInvoicesFilters] = useState<InvoicesFilters>(initalInvoiceFilter);
    const [unSelectedInvoicesFilters, setUnSelectedInvoicesFilters] = useState<InvoicesFilters>(initalInvoiceFilter);
    const [filterSearch, setFilterSearch] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        if (invoicePaymentResponse.invoicePayment?.invoices) {
            const filteredInvoices = invoicePaymentResponse.invoicePayment?.invoices.filter(
                (invoice: invoicePaymentInvoiceModel) => {
                    return (
                        selectedInvoicesFilters.invoiceNumber.includes(invoice.invoiceNumber) &&
                        selectedInvoicesFilters.invoiceDate.includes(invoice.invoiceDate) &&
                        selectedInvoicesFilters.dueDate.includes(invoice.dueDate) &&
                        selectedInvoicesFilters.customerPo.includes(invoice.customerPo) &&
                        selectedInvoicesFilters.shipTo.includes(invoice.shipTo) &&
                        selectedInvoicesFilters.balanceDueDisplay.includes(invoice.balanceDueDisplay) &&
                        (selectedInvoicesFilters.termsDiscountDate.includes(invoice.termsDiscountDate) ||
                            (selectedInvoicesFilters.termsDiscountDate.includes("blank") && !invoice.termsDiscountDate))
                    );
                },
            );
            setInvoices(filteredInvoices);
        }
    }, [selectedInvoicesFilters]);

    useEffect(() => {
        if (invoicePaymentResponse.invoicePayment?.invoices) {
            setInvoices(invoicePaymentResponse.invoicePayment.invoices);
            setInvoicesFilters({
                invoiceNumber: prepareFilters("invoiceNumber"),
                invoiceDate: prepareFilters("invoiceDate"),
                dueDate: prepareFilters("dueDate"),
                customerPo: prepareFilters("customerPo"),
                shipTo: prepareFilters("shipTo"),
                balanceDueDisplay: prepareFilters("balanceDueDisplay"),
                termsDiscountDate: prepareFilters("termsDiscountDate"),
            });
            // In some cases invoicesFilters will not be ready to read from state
            // we have to prepare filters not reading from state
            setSelectedInvoicesFilters({
                invoiceNumber: prepareFilters("invoiceNumber"),
                invoiceDate: prepareFilters("invoiceDate"),
                dueDate: prepareFilters("dueDate"),
                customerPo: prepareFilters("customerPo"),
                shipTo: prepareFilters("shipTo"),
                balanceDueDisplay: prepareFilters("balanceDueDisplay"),
                termsDiscountDate: prepareFilters("termsDiscountDate"),
            });
        }
    }, [invoicePaymentResponse.invoicePayment]);

    useEffect(() => {
        if (!detectIsAuthenticated(session)) {
            return;
        }

        loadOpenInvoicePayment();
        loadCurrentCart({
            additionalExpands: ["invoicePaymentPage"],
        } as any);

        setFilterSearch({
            invoiceNumber: "",
            invoiceDate: "",
            dueDate: "",
            customerPo: "",
            shipTo: "",
            balanceDueDisplay: "",
            termsDiscountDate: "",
        });
    }, []);

    useEffect(() => {
        if (!detectIsAuthenticated(session)) {
            return;
        }

        updateCustomMethod();
    }, [prevPaymentOptions]);

    useEffect(() => {
        if (paymentMethod && !invoicePaymentResponse.isLoading) {
            if (!detectIsAuthenticated(session)) {
                return;
            }
            if (validateLateFee()) {
                getLateFeeTotal(paymentAmountValues);
            }
        }
    }, [paymentMethod]);

    const validateLateFee = () => {
        if (
            (applyLateFeeOnItems && applyLateFeeOnItems.includes(paymentMethod?.name)) ||
            paymentMethod?.isPaymentProfile
        ) {
            return true;
        }

        dispatch({
            type: "Data/InvoicePayment/ClearLateFeeTotal",
        });
        return false;
    };

    const getLateFeeTotal = (paymentValues: { [key: string]: any }) => {
        const InvoicePayments = [] as any;

        Object.keys(paymentValues).forEach(key => {
            if (paymentValues[key].value) {
                InvoicePayments.push({
                    invoiceNumber: key,
                    amount: paymentAmountValues[key].value,
                    remark: paymentAmountValues[key].remark,
                });
            }
        });

        if (!Object.keys(InvoicePayments).length) {
            return;
        }

        xGetLateFeeTotal({
            InvoicePayments,
            PaymentMethod: customPaymentMethods?.find(p => p.name === paymentMethod.name),
        } as any);
    };

    const updateCustomMethod = () => {
        const customPaymentMethods: any = [];
        if (cart?.paymentOptions) {
            const allowedPaymentMethodsForInvoicePayment: string[] =
                (settings?.customSettings && (settings?.customSettings.allowedPaymentMethodsForInvoicePayment as [])) ||
                [];
            const paymentMethods = cart?.paymentOptions?.paymentMethods || [];
            paymentMethods.forEach(paymentMethod => {
                if (allowedPaymentMethodsForInvoicePayment.indexOf(paymentMethod.name) > -1) {
                    customPaymentMethods.push(paymentMethod);
                } else if (paymentMethod.isCreditCard || paymentMethod.isPaymentProfile) {
                    if (
                        allowedPaymentMethodsForInvoicePayment.indexOf(
                            settings?.customSettings.creditCardPaymentMethodName,
                        ) > -1
                    ) {
                        customPaymentMethods.push(paymentMethod);
                    }
                }
            });
        }

        setCustomPaymentMethods(customPaymentMethods);
    };

    const dispatch = useDispatch();
    const resetForm = () => {
        dispatch({
            type: "Data/InvoicePayment/ClearLateFeeTotal",
        });
        dispatch({
            type: "Data/InvoicePayment/ChangePaymentMethod",
            paymentMethod: undefined,
        });
        setApiError("");
        setPaymentAmountValues({});
        setPaymentAmountErrors({});
        setCustomPaymentMethods([]);
        updateCustomMethod();
    };

    const loadOpenInvoicePayment = () => {
        xGetOpenInvoicePayment({
            onError: errorMessage => {
                setApiError(errorMessage);
            },
            onComplete: () => {
                initializeScrollTo("[data-test-selector*='_errorApi']", -50);
            },
        });
    };

    const validateNegativeValue = (value: string, shouldBeNegative: boolean) => {
        if (!shouldBeNegative || !value) {
            if ((value.match(new RegExp("-", "g")) || []).length > 0) {
                return false;
            }
            return true;
        }
        // allow 0 value
        if (value === "0") {
            return true;
        }
        // add dash in the first character only
        if (value.charAt(0) !== "-") {
            return false;
        }
        // prevent multiple dash
        if ((value.match(new RegExp("-", "g")) || []).length > 1) {
            return false;
        }
        return true;
    };

    const onChangePaymentMount = (
        event: React.ChangeEvent<HTMLInputElement>,
        invoiceNumber: string,
        balanceDue: number,
    ) => {
        const values = { ...paymentAmountValues };
        const oldValue = paymentAmountValues[invoiceNumber] ?? "";
        const value = event.target.value;
        const regex = /^[0-9.-]*$/;
        const minValue = balanceDue < 0 ? balanceDue : 0;
        const maxValue = balanceDue < 0 ? 0 : balanceDue;
        const allowNegative = validateNegativeValue(value, minValue === balanceDue);
        if (value !== "" && regex.test(value) && allowNegative) {
            if (value === "-" || (parseFloat(value) <= maxValue && parseFloat(value) >= minValue)) {
                setPaymentAmountValues({
                    ...paymentAmountValues,
                    [invoiceNumber]: {
                        ...paymentAmountValues[invoiceNumber],
                        value,
                    },
                });

                values[invoiceNumber] = {
                    ...paymentAmountValues[invoiceNumber],
                    value,
                };
            } else {
                event.preventDefault();
            }
        } else {
            setPaymentAmountValues({
                ...paymentAmountValues,
                [invoiceNumber]: value === "" ? { ...paymentAmountValues[invoiceNumber], value } : oldValue,
            });

            values[invoiceNumber] = value === "" ? { ...paymentAmountValues[invoiceNumber], value } : oldValue;
        }
    };

    const onBlurPaymentMount = (
        event: React.ChangeEvent<HTMLInputElement>,
        invoiceNumber: string,
        balanceDue: number,
    ) => {
        validateLateFee() && getLateFeeTotal(paymentAmountValues);

        const value = event.target.value;
        if (
            !paymentAmountValues[invoiceNumber]?.remark &&
            value !== "" &&
            value !== "0" &&
            Number(value) !== balanceDue
        ) {
            setRemarkModal({ isOpen: true, invoiceNumber, remark: paymentAmountValues[invoiceNumber]?.remark });
        }
    };

    const isError = () => {
        for (const [key, value] of Object.entries(paymentAmountErrors)) {
            if (value) {
                return true;
            }
        }
        return false;
    };

    const calculateTotal = () => {
        let total = 0;

        if (isError()) {
            return translate("Please check your values");
        }

        for (const [key, data] of Object.entries(paymentAmountValues)) {
            if (!data.value || !isNumeric(data.value.toString())) {
                continue;
            }
            total += Number(data.value);
        }
        return total.toFixed(2);
    };

    const calculateTotalWithLate = () => {
        if (isError()) {
            return translate("Please check your values");
        }
        const total = calculateTotal() as any;
        if (!Number.isNaN(total) && lateFee) {
            return (Number(total) + Number(lateFee)).toFixed(2);
        }
        return total;
    };

    const addCurrency = (number: number | null) => {
        if (isError() || (number !== null && Number.isNaN(number))) {
            return translate("Please check your values");
        }
        number = Number(number);
        return (
            Number(number.toFixed(2)).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
            }) || "$0.00"
        );
    };

    const isMakePaymentDisabled = () => {
        const invoicePayments = [];
        Object.keys(paymentAmountValues).forEach((key: any) => {
            const number = paymentAmountValues[key]?.value !== "" ? Number(paymentAmountValues[key].value) : 0;
            if (!Number.isNaN(number) && number !== 0) {
                invoicePayments.push(paymentAmountValues[key].value);
            }
        });
        return invoicePayments.length === 0;
    };

    // called from CreditCardIFrame component
    const makePaymentSubmit = (data: any) => {
        const InvoicePayments = [] as any;
        Object.keys(paymentAmountValues).forEach((key: any) => {
            const number = paymentAmountValues[key]?.value !== "" ? Number(paymentAmountValues[key].value) : 0;
            if (!Number.isNaN(number) && number !== 0) {
                InvoicePayments.push({
                    invoiceNumber: key,
                    amount: paymentAmountValues[key].value,
                    remark: paymentAmountValues[key].remark,
                });
            }
        });
        if (isError()) {
            initializeScrollTo("[data-test-selector*='-error']", -50);
            return;
        }
        if (InvoicePayments.length === 0) {
            setNoAmountsEnteredError(translate("Please enter payment amounts to submit"));
            return;
        }
        setNoAmountsEnteredError("");

        const parameter = {
            ...data,
            InvoicePayments,
            AchAccountNumber: selectedAchAccountNumber,
            BankGuid: selectedAch?.bankGuid,
        };

        xMakePaymentInvoicePayment({
            parameter,
            onError: (errorMessage: string) => {
                setApiError(errorMessage);
            },
            onSuccess: () => {
                resetForm();
            },
            onComplete: () => {
                loadOpenInvoicePayment();
            },
        } as any);
    };

    const formatAmount = (amount = 0) => {
        const newAmount = Number(`${Math.round(Number(`${amount}e2`))}e-2`).toFixed(2);
        return newAmount;
    };

    const makePaymentHandler = () => {
        (childRef.current as any)?.makeCreditCardPayment();
    };

    const payInFullHandler = (invoiceNumber: string, balanceDue: number) => {
        const paymentValues = {
            ...paymentAmountValues,
            [invoiceNumber]: { value: formatAmount(balanceDue) },
        };
        setPaymentAmountValues(paymentValues);
        setPaymentAmountErrors({
            ...paymentAmountErrors,
            [invoiceNumber]: "",
        });
        if (validateLateFee()) {
            getLateFeeTotal(paymentValues);
        }
    };

    const payAllInvoicesHandler = () => {
        let newState = {} as any;

        invoices.forEach((invoice: any) => {
            const { invoiceNumber, balanceDue } = invoice;
            newState = {
                ...newState,
                [invoiceNumber]: { value: formatAmount(balanceDue) },
            };
        });
        setPaymentAmountErrors({});
        setPaymentAmountValues(newState);
        if (validateLateFee()) {
            getLateFeeTotal(newState);
        }
    };

    let remarkDelay: number;

    const handlePaymentRemarkChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        clearTimeout(remarkDelay);
        remarkDelay = setTimeout(
            (value: string) =>
                setRemarkModal(modal => {
                    modal.remark = value;
                    return modal;
                }),
            250,
            event.target.value,
        );
    };

    const handleAddRemark = (invoiceNumber: string) => {
        if (!remarkModal.remark) {
            return;
        }

        setPaymentAmountValues({
            ...paymentAmountValues,
            [invoiceNumber]: {
                ...paymentAmountValues[invoiceNumber],
                remark: remarkModal.remark,
            },
        });
        setRemarkModal(modal => {
            modal.isOpen = false;
            return modal;
        });
    };

    if (!detectIsAuthenticated(session)) {
        return (
            <StyledWrapper data-test-selector="invoicePayment_loading" {...style.notifyMessage.wrapper}>
                <Notify messageType="danger" body={siteMessage("InvoicePayment_SignIn", "You must SignIn")} />
            </StyledWrapper>
        );
    }

    // Render
    if (!invoicePaymentResponse.invoicePayment && invoicePaymentResponse.isLoading) {
        return (
            <StyledWrapper data-test-selector="invoicePayment_loading" {...style.loading.wrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // Check if the clicked element or any of its ancestors have the class "invoice-filter"
        if (!target.closest(".invoice-filter")) {
            setOpenedFilter("");
            document.removeEventListener("click", handleDocumentClick);
            setUnSelectedInvoicesFilters(initalInvoiceFilter);
        }
    };

    const toggleFilter = (event: React.MouseEvent, columnName: string) => {
        event.stopPropagation();
        if (openedFilter === columnName) {
            setOpenedFilter("");
            setUnSelectedInvoicesFilters(initalInvoiceFilter);
        } else {
            setOpenedFilter(columnName);
            document.addEventListener("click", handleDocumentClick);
        }
    };

    const isOpenedFilter = (columnName: string) => {
        return openedFilter === columnName;
    };

    const prepareFilters = <K extends keyof invoicePaymentInvoiceModel>(columnName: K): string[] => {
        const filteredValues = invoicePaymentResponse.invoicePayment?.invoices.map(
            (invoice: invoicePaymentInvoiceModel) => {
                const value = invoice[columnName];
                return value !== null ? `${value}` : "blank";
            },
        );

        // Ensure "blank" appears only once in the array
        const uniqueFilteredValues = Array.from(new Set<string>(filteredValues || []));

        // Sorting filters because we can't compare un-sorted filters
        switch (columnName) {
            case "invoiceNumber":
                uniqueFilteredValues.sort((a, b) => a.localeCompare(b, "en", { numeric: true })); // Sort alpha-numerically
                break;
            case "invoiceDate":
            case "dueDate":
            case "termsDiscountDate":
                uniqueFilteredValues.sort((a, b) => {
                    // If either value is 'blank', treat it as the smallest value
                    if (a === "blank" || b === "blank") {
                        if (a === "blank" && b === "blank") {
                            return 0;
                        }
                        if (a === "blank") {
                            return -1;
                        }
                        return 1;
                    }
                    // Convert the values to dates for comparison
                    const dateA = new Date(a).getTime();
                    const dateB = new Date(b).getTime();
                    return dateA - dateB;
                });
                break;
            case "balanceDueDisplay":
                uniqueFilteredValues.sort(
                    (a, b) => parseFloat(a.replace(/[^\d.-]/g, "")) - parseFloat(b.replace(/[^\d.-]/g, "")),
                ); // Sort by balance ascending
                break;
            case "shipTo":
                uniqueFilteredValues.sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })); // Sort alphabetically A-Z
                break;
            case "customerPo":
                uniqueFilteredValues.sort((a, b) => a.trim().localeCompare(b.trim(), "en", { numeric: true })); // Sort alpha-numerically
                break;
            default:
        }
        return uniqueFilteredValues;
    };

    const getFilters = <K extends keyof InvoicesFilters>(columnName: K): string[] => {
        const currentInvoices = invoices;

        // Map the invoices to get the values for the specified column
        const filters = currentInvoices.map(invoice => {
            return `${invoice[columnName] || "blank"}`; // Use 'blank' if the value is null
        });

        // Include the unchecked filters from local state
        const uncheckedFilters = unSelectedInvoicesFilters[columnName];

        if (uncheckedFilters && uncheckedFilters.length > 0) {
            uncheckedFilters.forEach(filter => {
                const index = filters.indexOf(filter);
                if (index === -1) {
                    filters.push(filter);
                }
            });
        }

        // Remove duplicate values and sort them as needed
        const uniqueFilters = Array.from(new Set(filters));

        switch (columnName) {
            case "invoiceNumber":
                uniqueFilters.sort((a, b) => a.localeCompare(b, "en", { numeric: true })); // Sort alpha-numerically
                break;
            case "invoiceDate":
            case "dueDate":
            case "termsDiscountDate":
                uniqueFilters.sort((a, b) => {
                    // If either value is 'blank', treat it as the smallest value
                    if (a === "blank" || b === "blank") {
                        if (a === "blank" && b === "blank") {
                            return 0;
                        }
                        if (a === "blank") {
                            return -1;
                        }
                        return 1;
                    }
                    // Convert the values to dates for comparison
                    const dateA = new Date(a).getTime();
                    const dateB = new Date(b).getTime();
                    return dateA - dateB;
                });
                break;
            case "balanceDueDisplay":
                uniqueFilters.sort(
                    (a, b) => parseFloat(a.replace(/[^\d.-]/g, "")) - parseFloat(b.replace(/[^\d.-]/g, "")),
                ); // Sort by balance ascending
                break;
            case "shipTo":
                uniqueFilters.sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })); // Sort alphabetically A-Z
                break;
            case "customerPo":
                uniqueFilters.sort((a, b) => a.trim().localeCompare(b.trim(), "en", { numeric: true })); // Sort alpha-numerically
                break;
            default:
        }

        return uniqueFilters.filter((filter: string) =>
            filter.toLowerCase().includes(filterSearch[columnName].toLowerCase()),
        );
    };

    const onChangeFilterSearch = (event: React.ChangeEvent<HTMLInputElement>, key: FilterName) => {
        const searchTerm = event.target.value.toLowerCase();
        setFilterSearch({ ...filterSearch, [key]: searchTerm });
        const filters = prepareFilters(key).filter((filter: string) => filter.toLowerCase().includes(searchTerm));
        // Update the selected filters based on the filtered list of options
        setSelectedInvoicesFilters(prevState => {
            return { ...prevState, [key]: filters };
        });
    };

    const onChangeFilterHandler = (
        _: React.SyntheticEvent<Element, Event>,
        value: boolean,
        filterName: FilterName,
        filterValue: string,
    ) => {
        setSelectedInvoicesFilters(prevState => {
            const updatedFilters = [...prevState[filterName]];
            const filterIndex = updatedFilters.indexOf(filterValue);

            if (filterIndex > -1) {
                updatedFilters.splice(filterIndex, 1);
            } else {
                updatedFilters.push(filterValue);
            }

            switch (filterName) {
                case "invoiceNumber":
                    updatedFilters.sort((a, b) => a.localeCompare(b, "en", { numeric: true })); // Sort alpha-numerically
                    break;
                case "invoiceDate":
                case "dueDate":
                case "termsDiscountDate":
                    updatedFilters.sort((a, b) => {
                        // If either value is 'blank', treat it as the smallest value
                        if (a === "blank" || b === "blank") {
                            if (a === "blank" && b === "blank") {
                                return 0;
                            }
                            if (a === "blank") {
                                return -1;
                            }
                            return 1;
                        }
                        // Convert the values to dates for comparison
                        const dateA = new Date(a).getTime();
                        const dateB = new Date(b).getTime();
                        return dateA - dateB;
                    });
                    break;
                case "balanceDueDisplay":
                    updatedFilters.sort(
                        (a, b) => parseFloat(a.replace(/[^\d.-]/g, "")) - parseFloat(b.replace(/[^\d.-]/g, "")),
                    ); // Sort by balance ascending
                    break;
                case "shipTo":
                    updatedFilters.sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })); // Sort alphabetically A-Z
                    break;
                case "customerPo":
                    updatedFilters.sort((a, b) => a.trim().localeCompare(b.trim(), "en", { numeric: true })); // Sort alpha-numerically
                    break;
                default:
            }

            return {
                ...prevState,
                [filterName]: updatedFilters,
            };
        });
        setUnSelectedInvoicesFilters(prevState => {
            const updatedFilters = [...prevState[filterName]];
            const filterIndex = updatedFilters.indexOf(filterValue);

            if (filterIndex > -1) {
                updatedFilters.splice(filterIndex, 1);
            } else {
                updatedFilters.push(filterValue);
            }

            return {
                ...prevState,
                [filterName]: updatedFilters,
            };
        });
    };

    const onChangeAllSelectFilterHandler = (
        _: React.SyntheticEvent<Element, Event>,
        value: boolean,
        filterName: FilterName,
    ) => {
        setFilterSearch({ ...filterSearch, [filterName]: "" });
        setSelectedInvoicesFilters(prevState => ({
            ...prevState,
            [filterName]: value ? [] : invoicesFilters[filterName],
        }));
        setUnSelectedInvoicesFilters(prevState => ({
            ...prevState,
            [filterName]: value ? 
                invoicesFilters[filterName].filter(x => x !== "") :  // Filter out empty strings
                [],
        }));
    };

    const isFilterChecked = (filterName: keyof InvoicesFilters, filterValue: string): boolean => {
        return selectedInvoicesFilters[filterName].includes(filterValue);
    };

    const isAllFiltersChecked = (filterName: keyof InvoicesFilters): boolean => {
        return invoicesFilters[filterName].length === selectedInvoicesFilters[filterName].length;
    };

    const clearFilters = () => {
        setSelectedInvoicesFilters(invoicesFilters);
        setOpenedFilter("");
        setFilterSearch({
            invoiceNumber: "",
            invoiceDate: "",
            dueDate: "",
            customerPo: "",
            shipTo: "",
            balanceDueDisplay: "",
            termsDiscountDate: "",
        });
    };

    const shouldShowDiscountColumn = () => {
        return invoicePaymentResponse.invoicePayment?.invoices.some(
            (i: invoicePaymentInvoiceModel) => i.termsDiscountDate,
        );
    };

    const shouldShowClearFilterButton = () => {
        return (
            JSON.stringify(invoicesFilters) !== JSON.stringify(selectedInvoicesFilters) ||
            areObjectValuesNotEmpty(filterSearch)
        );
    };

    return (
        <LoadingOverlay data-test-selector="invoicePayment" loading={invoicePaymentResponse.isLoading}>
            <StyledWrapper {...titleStyles.justifiedWrapper}>
                <Typography variant="h2" as="h1" data-test-selector="PageTitle" {...titleStyles.titleText}>
                    {capitalize(billTo?.customerName ? `${billTo?.customerName} - ` : "")}
                    {pageTitle}
                </Typography>
                {!errorApi && invoicePaymentResponse.invoicePayment?.invoices.length > 0 && (
                    <Button data-test-selector="pay_all_invoices" color="secondary" onClick={payAllInvoicesHandler}>
                        {translate("Pay All Invoices")}
                    </Button>
                )}
            </StyledWrapper>
            {!invoicePaymentResponse.isLoading && !invoicePaymentResponse.invoicePayment?.invoices.length && (
                <Notify
                    data-test-selector="invoicePayment_emptyInvoices"
                    body={siteMessage("InvoicePayment_EmptyInvoices")}
                    messageType="danger"
                />
            )}
            {shouldShowClearFilterButton() && (
                <Button onClick={clearFilters} {...style.filters.clearAll}>
                    {translate("Clear All Filters")}
                </Button>
            )}
            {invoicePaymentResponse.invoicePayment?.invoices.length > 0 && (
                <>
                    <StyledWrapper
                        data-test-selector="invoicePayment_table"
                        {...(openedFilter ? style.tableWrapperWithFilter : style.tableWrapper)}
                    >
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Invoice ID")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "invoiceNumber")}
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("invoiceNumber") && (
                                            <InvoiceFilter
                                                filterName="invoiceNumber"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Invoice Date")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "invoiceDate")}
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("invoiceDate") && (
                                            <InvoiceFilter
                                                filterName="invoiceDate"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Due Date")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "dueDate")}
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("dueDate") && (
                                            <InvoiceFilter
                                                filterName="dueDate"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                {shouldShowDiscountColumn() && (
                                    <DataTableHeader tight {...style.balanceDueCell}>
                                        <StyledWrapper {...style.tableHeaderWrapper} className="wrapHead">
                                            {translate("Discount Due Date")}
                                            <Clickable
                                                onClick={(event: React.MouseEvent) =>
                                                    toggleFilter(event, "termsDiscountDate")
                                                }
                                                {...style.filters.filterButton}
                                            >
                                                <Icon src={Filter} {...style.filters.filterIcon} />
                                            </Clickable>
                                            {isOpenedFilter("termsDiscountDate") && (
                                                <InvoiceFilter
                                                    filterName="termsDiscountDate"
                                                    filterSearch={filterSearch}
                                                    onChangeFilterSearch={onChangeFilterSearch}
                                                    onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                    onChangeFilterHandler={onChangeFilterHandler}
                                                    isAllFiltersChecked={isAllFiltersChecked}
                                                    isFilterChecked={isFilterChecked}
                                                    getFilters={getFilters}
                                                    toggleFilter={toggleFilter}
                                                    style={style}
                                                />
                                            )}
                                        </StyledWrapper>
                                    </DataTableHeader>
                                )}
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("PO#")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "customerPo")}
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("customerPo") && (
                                            <InvoiceFilter
                                                filterName="customerPo"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight {...style.shipToCell.wrapper}>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Ship To")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "shipTo")}
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("shipTo") && (
                                            <InvoiceFilter
                                                filterName="shipTo"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight {...style.balanceDueCell}>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Balance Due")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) =>
                                                toggleFilter(event, "balanceDueDisplay")
                                            }
                                            {...style.filters.filterButton}
                                        >
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("balanceDueDisplay") && (
                                            <InvoiceFilter
                                                filterName="balanceDueDisplay"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style}
                                            />
                                        )}
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight {...style.emptyCell}></DataTableHeader>
                                <DataTableHeader tight {...style.paymentAmountCell}>
                                    {translate("Payment Amount")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {invoices.map(
                                    ({
                                        invoiceNumber,
                                        invoiceDate,
                                        dueDate,
                                        customerPo,
                                        shipTo,
                                        balanceDue,
                                        balanceDueDisplay,
                                        isPendingPartialPayment,
                                        termsDiscountDate,
                                    }: any) => (
                                        <DataTableRow key={invoiceNumber}>
                                            <DataTableCell {...style.columnCell}>
                                                <Link
                                                    href={`/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`}
                                                    data-test-selector="invoiceHistory_invoiceLine_number"
                                                >
                                                    {invoiceNumber}
                                                </Link>
                                            </DataTableCell>
                                            <DataTableCell {...style.columnCell}>{invoiceDate}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{dueDate}</DataTableCell>
                                            {shouldShowDiscountColumn() && (
                                                <DataTableCell>{termsDiscountDate}</DataTableCell>
                                            )}
                                            <DataTableCell {...style.columnCell}>{customerPo}</DataTableCell>
                                            <DataTableCell {...style.shipToCell.wrapper}>{shipTo}</DataTableCell>
                                            <DataTableCell {...style.balanceDueCell}>
                                                <StyledWrapper {...style.balanceDueWrapper}>
                                                    <Typography>{balanceDueDisplay}</Typography>
                                                    {isPendingPartialPayment && (
                                                        <Typography>
                                                            {siteMessage("RUGBY_PendingPartialPayment")}
                                                        </Typography>
                                                    )}
                                                </StyledWrapper>
                                            </DataTableCell>
                                            <DataTableCell {...style.emptyCell} tight></DataTableCell>
                                            <DataTableCell className="value-cell" {...style.paymentAmountCell}>
                                                <TextField
                                                    type="text"
                                                    value={
                                                        paymentAmountValues[`${invoiceNumber.toString()}`]?.value ?? ""
                                                    }
                                                    error={paymentAmountErrors[`${invoiceNumber.toString()}`] || ""}
                                                    {...style.textFieldStyle}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                        onChangePaymentMount(event, invoiceNumber, balanceDue)
                                                    }
                                                    onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                        onBlurPaymentMount(event, invoiceNumber, balanceDue)
                                                    }
                                                />
                                                <Button
                                                    variant="secondary"
                                                    data-test-selector="pay_in_full"
                                                    css={css`
                                                        width: 100%;
                                                        margin-top: 15px;
                                                    `}
                                                    onClick={() => payInFullHandler(invoiceNumber, balanceDue)}
                                                >
                                                    {translate("Pay In Full")}
                                                </Button>
                                                {paymentAmountValues[`${invoiceNumber.toString()}`]?.remark && (
                                                    <Button
                                                        variant="primary"
                                                        css={css`
                                                            width: 100%;
                                                            margin-top: 15px;
                                                        `}
                                                        onClick={() =>
                                                            setRemarkModal({
                                                                isOpen: true,
                                                                invoiceNumber,
                                                                remark: paymentAmountValues[invoiceNumber]?.remark,
                                                            })
                                                        }
                                                    >
                                                        {siteMessage("InvoicePayment_IncompletePaymentEdit")}
                                                    </Button>
                                                )}
                                            </DataTableCell>
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>

                    <Modal
                        headline={siteMessage("InvoicePayment_IncompletePaymentTitle")}
                        isOpen={remarkModal.isOpen}
                        isCloseable={false}
                    >
                        <GridContainer>
                            <GridItem width={12}>
                                <TextArea
                                    {...style.addRemark}
                                    defaultValue={remarkModal.remark}
                                    onChange={handlePaymentRemarkChange}
                                    maxLength={300}
                                    required
                                />
                            </GridItem>
                            <GridItem width={12}>
                                <Notify
                                    {...style.incompletePaymentNote}
                                    body={siteMessage("InvoicePayment_IncompletePaymentNote")}
                                    messageType="warning"
                                />
                            </GridItem>
                            <GridItem {...style.addRemarkActions} width={12}>
                                <Button onClick={() => handleAddRemark(remarkModal.invoiceNumber)}>
                                    {siteMessage("InvoicePayment_IncompletePaymentButton")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </Modal>

                    <StyledWrapper {...style.ccAndPaymentWrapper}>
                        <StyledWrapper {...style.creditCardWrapper} data-test-selector="makePayment_creditCard">
                            {validateLateFee() && (
                                <StyledWrapper {...style.applyLateFeeMessage}>
                                    {siteMessage("Late_Fee_Message")}
                                </StyledWrapper>
                            )}
                            {customPaymentMethods.length > 0 ? (
                                <CreditCardIFrame
                                    ref={childRef}
                                    makePaymentSubmit={makePaymentSubmit}
                                    customPaymentMethods={customPaymentMethods}
                                />
                            ) : (
                                <Notify
                                    messageType="danger"
                                    body={siteMessage("Custom_InvoicePayement_NO_Payment_Methods")}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper data-test-selector="invoicePayment_result" {...style.totalWrapper}>
                            <TotalElement
                                dataTestSelector="total_balanceDue"
                                title="Total Balance Due"
                                value={invoicePaymentResponse.invoicePayment?.totalBalanceDue || ""}
                            />
                            <StyledWrapper className="total_paymentAmount">
                                <TotalElement
                                    dataTestSelector="total_paymentAmount"
                                    title={translate("Payment Amount")}
                                    value={`${
                                        Number.isNaN(calculateTotal() as any)
                                            ? calculateTotal()
                                            : addCurrency(Number(calculateTotal()))
                                    }`}
                                    isError={isError()}
                                />
                                {validateLateFee() && (
                                    <>
                                        <TotalElement
                                            dataTestSelector="surcharge_Amount"
                                            title={translate("Surcharge Amount")}
                                            value={`${addCurrency(lateFee || 0) || "0.00"}`}
                                            isError={isError()}
                                        />
                                        <TotalElement
                                            dataTestSelector="total_surcharge_Amount"
                                            title={translate("Total Payment")}
                                            value={`${addCurrency(calculateTotalWithLate()) || "0.00"}`}
                                            isError={isError()}
                                        />
                                    </>
                                )}
                                <StyledWrapper data-test-selector="makePayment">
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                        `}
                                    >
                                        <Button
                                            {...style.makePaymentBtn}
                                            data-test-selector="makePayment_submit"
                                            disabled={
                                                isMakePaymentDisabled() ||
                                                calculateTotalWithLate() < 0 ||
                                                isError() ||
                                                !Object.keys(paymentAmountValues).length ||
                                                (paymentMethodName === achPaymentMethodName &&
                                                    (!!achError || !selectedAchAccountNumber || achIsEditing)) ||
                                                !customPaymentMethods.length
                                            }
                                            onClick={makePaymentHandler}
                                        >
                                            {translate("Make Payment")}
                                        </Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper
                                css={css`
                                    flex-basis: 100%;
                                    display: flex;
                                    flex-direction: column;
                                    justify-content: flex-start;
                                    align-items: flex-end;
                                    max-width: 400px;
                                    margin-top: 30px;
                                `}
                            >
                                {calculateTotalWithLate() < 0 && (
                                    <Notify
                                        style={{ marginBottom: "20px" }}
                                        messageType="danger"
                                        body={translate("Total payment amount is invalid.")}
                                    />
                                )}
                                {noAmountsEnteredError && (
                                    <Notify
                                        style={{ marginBottom: "20px" }}
                                        messageType="danger"
                                        body={noAmountsEnteredError}
                                    />
                                )}
                                {errorApi && (
                                    <Notify
                                        style={{ marginBottom: "20px" }}
                                        data-test-selector="invoicePayment_errorApi"
                                        body={errorApi}
                                        messageType="danger"
                                    />
                                )}
                                {achError && paymentMethodName === achPaymentMethodName && (
                                    <Notify messageType="danger" body={achError} />
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
        </LoadingOverlay>
    );
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Invoice Payment Widget",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentWidget),
    definition,
};

export default widgetModule;
